// ─────────────────────────────────────────────────────────────────────────────
// Tokens
// ─────────────────────────────────────────────────────────────────────────────
export const colors = {
  bg: "#F5F2EA",
  surface: "#FFFFFF",
  border: "#E5E1D5",
  borderStrong: "#D6D1C2",
  text: "#1A1A1A",
  textMuted: "#5C5C5C",
  textSoft: "#8A8A82",

  stages: ["#D04638", "#E08A1E", "#2A75B0", "#2C8D5E"],
  stagesSoft: ["#FBE8E5", "#FBEFD9", "#E2EDF7", "#DEEEE5"],

  good: "#1F8A4F",
  goodSoft: "#E2F1E8",
  bad: "#C53D2E",
  badSoft: "#FBE5E2",
  neutral: "#6B6B6B",
  neutralSoft: "#ECEAE2",
};

export const fonts = {
  body: "'Geist', -apple-system, sans-serif",
  mono: "'Geist Mono', monospace",
};

// CSS global (fonts + keyframes). Injetar via <style> no componente.
export const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  @keyframes ciclo-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(44, 141, 94, 0.55); }
    70%  { box-shadow: 0 0 0 8px rgba(44, 141, 94, 0); }
    100% { box-shadow: 0 0 0 0 rgba(44, 141, 94, 0); }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Page wrapper
// ─────────────────────────────────────────────────────────────────────────────
export const page = {
  background: colors.bg,
  minHeight: "100vh",
  padding: "24px 32px 20px",
  fontFamily: fonts.body,
  color: colors.text,
  fontFeatureSettings: "'tnum' 1, 'cv11' 1, 'ss01' 1",
  WebkitFontSmoothing: "antialiased",
};

export const dash = {
  display: "flex",
  flexDirection: "column",
  gap: 18,
  maxWidth: 1920,
  margin: "0 auto",
};

// ─────────────────────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────────────────────
export const header = {
  wrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingBottom: 2,
    flexWrap: "wrap",
    gap: 16,
  },
  title: {
    fontSize: 38,
    fontWeight: 800,
    letterSpacing: "-0.025em",
    lineHeight: 1,
    marginBottom: 8,
    margin: 0,
  },
  subtitle: {
    fontSize: 15,
    color: colors.textMuted,
    letterSpacing: "0.01em",
    marginTop: 8,
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  pill: (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    padding: "8px 16px",
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: colors.textMuted,
    cursor: active ? "default" : "pointer",
    userSelect: "none",
  }),
  pillDot: (loading) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: loading ? colors.stages[1] : colors.stages[3],
    animation: loading ? "ciclo-pulse 1.2s infinite" : "ciclo-pulse 1.8s infinite",
  }),
  clock: {
    fontFamily: fonts.mono,
    fontSize: 30,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.02em",
    color: colors.text,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Distribuição
// ─────────────────────────────────────────────────────────────────────────────
export const distribution = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "22px 28px 24px",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 18,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
  totalBlock: {
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    padding: "8px 16px",
    background: colors.bg,
    borderRadius: 10,
  },
  totalLabel: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.1em",
    color: colors.textMuted,
  },
  totalValue: {
    fontFamily: fonts.mono,
    fontWeight: 600,
    fontSize: 20,
    color: colors.text,
    letterSpacing: "-0.01em",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 14,
    marginBottom: 14,
  },
  stage: (i) => ({
    padding: "16px 18px",
    borderRadius: 12,
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    position: "relative",
    overflow: "hidden",
    borderLeft: `4px solid ${colors.stages[i]}`,
  }),
  stageLabel: (i) => ({
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.12em",
    marginBottom: 8,
    color: colors.stages[i],
  }),
  stageValue: {
    fontSize: 36,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.025em",
    lineHeight: 1,
    color: colors.text,
  },
  stageUnit: {
    fontSize: 18,
    fontWeight: 500,
    color: colors.textMuted,
    marginLeft: 4,
  },
  stagePct: {
    fontSize: 14,
    color: colors.textMuted,
    marginTop: 6,
    fontWeight: 500,
  },
  bar: {
    display: "flex",
    height: 16,
    borderRadius: 8,
    overflow: "hidden",
    border: `1px solid ${colors.border}`,
  },
  barSeg: (i, width) => ({
    background: colors.stages[i],
    width: `${width}%`,
    transition: "width 0.6s ease",
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Cards de etapa
// ─────────────────────────────────────────────────────────────────────────────
export const cards = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 16,
  },
  card: {
    background: colors.surface,
    borderRadius: 18,
    border: `1px solid ${colors.border}`,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  accent: (i) => ({
    height: 6,
    width: "100%",
    background: colors.stages[i],
  }),
  head: {
    padding: "20px 22px 18px",
    borderBottom: `1px solid ${colors.border}`,
  },
  stageTag: {
    display: "inline-block",
    fontSize: 12,
    letterSpacing: "0.12em",
    color: colors.textSoft,
    fontWeight: 600,
    marginBottom: 6,
  },
  title: (i) => ({
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: "-0.015em",
    marginBottom: 10,
    lineHeight: 1,
    color: colors.stages[i],
    margin: 0,
  }),
  count: {
    fontSize: 15,
    color: colors.textMuted,
    fontVariantNumeric: "tabular-nums",
  },
  countStrong: {
    fontWeight: 700,
    color: colors.text,
  },
  badges: {
    marginTop: 10,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.05em",
    color: colors.textMuted,
    background: colors.bg,
    padding: "5px 10px",
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
  },
  badgeLabel: {
    color: colors.textSoft,
    fontWeight: 700,
    letterSpacing: "0.08em",
  },
  badgeValue: {
    color: colors.text,
    fontFamily: fonts.mono,
    fontWeight: 600,
  },
  badgeSoft: {
    color: colors.textSoft,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Metrics 2x2
// ─────────────────────────────────────────────────────────────────────────────
export const metrics = {
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    flex: 1,
  },
  cell: (idx) => {
    const isOdd = idx % 2 === 0; // 0,2 → coluna esquerda
    const isTopRow = idx < 2;
    return {
      padding: "18px 18px 20px",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      borderRight: isOdd ? `1px solid ${colors.border}` : "none",
      borderBottom: isTopRow ? `1px solid ${colors.border}` : "none",
    };
  },
  label: {
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: "0.05em",
    color: colors.text,
    lineHeight: 1.35,
    marginBottom: 14,
    minHeight: 32,
  },
  value: (state) => {
    const map = {
      below: colors.good,
      above: colors.bad,
      equal: colors.neutral,
    };
    return {
      fontSize: 34,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      letterSpacing: "-0.025em",
      lineHeight: 1,
      color: state ? map[state] : colors.text,
    };
  },
  unit: {
    fontSize: 15,
    fontWeight: 500,
    color: colors.textMuted,
    marginLeft: 3,
  },
  compare: (state) => {
    const map = {
      below: { bg: colors.goodSoft, color: colors.good },
      above: { bg: colors.badSoft, color: colors.bad },
      equal: { bg: colors.neutralSoft, color: colors.neutral },
    };
    const c = map[state] || map.equal;
    return {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      marginTop: 8,
      fontSize: 12,
      fontWeight: 600,
      padding: "4px 8px",
      borderRadius: 5,
      letterSpacing: "0.01em",
      alignSelf: "flex-start",
      background: c.bg,
      color: c.color,
    };
  },
  compareArrow: {
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Glossário
// ─────────────────────────────────────────────────────────────────────────────
export const glossary = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 15,
    padding: "15px 20px 17px",
  },
  head: {
    marginBottom: 12,
    display: "flex",
    alignItems: "baseline",
    gap: 12,
    flexWrap: "wrap",
  },
  title: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
  sub: {
    fontSize: 11,
    color: colors.textMuted,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 15,
  },
  item: {
    borderLeft: `3px solid ${colors.borderStrong}`,
    padding: "3px 0 3px 12px",
  },
  label: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: colors.text,
    marginBottom: 4,
    lineHeight: 1.3,
  },
  help: {
    fontSize: 11,
    color: colors.textMuted,
    lineHeight: 1.4,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Footer
// ─────────────────────────────────────────────────────────────────────────────
export const footer = {
  wrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 6px 0",
    fontSize: 14,
    color: colors.textMuted,
    flexWrap: "wrap",
    gap: 12,
  },
  legend: {
    display: "flex",
    gap: 22,
    flexWrap: "wrap",
    alignItems: "center",
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 600,
    color: colors.text,
    fontSize: 13,
    letterSpacing: "0.04em",
  },
  legendCompare: {
    fontWeight: 500,
    color: colors.textMuted,
    letterSpacing: 0,
  },
  swatch: (bg) => ({
    width: 12,
    height: 12,
    borderRadius: 3,
    background: bg,
  }),
  divider: {
    width: 1,
    height: 16,
    background: colors.borderStrong,
  },
  schedule: {
    fontSize: 13,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
export function compareState(value, baseline) {
  if (value == null || baseline == null) return null;
  if (value < baseline) return "below";
  if (value > baseline) return "above";
  return "equal";
}
